import React, { useEffect } from 'react';
import { Listing } from '../types';
import { availabilityLabel, bedroomsLabel, facts, furnishingLabel, money, providerName, sqft } from '../format';
import { openLink, showBackButton } from '../telegram';
import { ListingPhoto } from './ListingCard';

type Props = {
  listing: Listing;
  onBack: () => void;
};

function listedOn(listing: Listing): string {
  const names = listing.sources.map(source => providerName(source.provider));
  if (names.length > 1) return `Listed on ${names.join(' and ')}. We merged the duplicates.`;
  return `Listed on ${names[0]}.`;
}

export default function ListingDetail({ listing, onBack }: Props) {
  useEffect(() => showBackButton(onBack), [onBack]);

  const mainSource = listing.sources[0];
  const { latitude, longitude } = listing.coordinates;
  const grid = [
    ['Bedrooms', bedroomsLabel(listing.bedrooms)],
    ['Bathrooms', listing.bathrooms ?? 'Not stated'],
    ['Size', listing.sizeSqm !== null ? `${listing.sizeSqm} m² · ${sqft(listing.sizeSqm)} sq ft` : 'Not stated'],
    ['Available', availabilityLabel(listing.availability)],
    ['Furnishing', furnishingLabel(listing.furnishTypes)],
    ['Deposit', listing.deposit !== null ? money(listing.deposit) : 'Not stated'],
  ];

  return (
    <>
      <header className="ma-header">
        <button type="button" className="ma-link-button" onClick={onBack}>Back</button>
      </header>
      <ListingPhoto listing={listing} showNew={false} />
      <main className="ma-body ma-stack">
        <div>
          <div className="ma-price">{money(listing.monthlyPrice)} <small>pcm</small></div>
          <div className="ma-facts">{facts(listing)}</div>
          <div className="ma-address">{listing.address}</div>
        </div>
        <dl className="ma-card ma-grid" style={{ margin: 0 }}>
          {grid.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
        <div className="ma-card ma-row" style={{ marginBottom: 0 }}>
          <span className="ma-facts">{listing.postcode}</span>
          <button
            type="button"
            className="ma-link-button"
            onClick={() => openLink(`https://maps.google.com/?q=${latitude},${longitude}`)}
          >
            Open in Maps
          </button>
        </div>
        <p className="ma-hint">{listedOn(listing)}</p>
      </main>
      {mainSource && (
        <footer className="ma-footer">
          <button type="button" className="ma-primary" onClick={() => openLink(mainSource.url)}>
            View on {providerName(mainSource.provider)}
          </button>
        </footer>
      )}
    </>
  );
}
